import { useCallback, useEffect, useMemo, useState } from "react";
import { relatorioAdmin } from "../../servicos/relatorioAdmin";
import { filiadoAPI } from "../../servicos/filiado";
import ListagemFeedback from "../Listagem/feedback";
import ListagemOpcoes from "../Listagem/opcoes";
import Topo from "../Topo/topo";
import Paginacao from "../Listagem/paginacao";
import { formatarMoedaBRL, normalizarValorNumerico } from "../../constantes/relatorios";
import { DataTable } from "../ui/data-table";
import ColumnToggleDropdown from "../ui/column-toggle-dropdown";
import { exportarXLSX } from "../../utilitarios/excel";

function TabelaRelatorioIntencaoVenda() {

    const date = new Date();
    const STATUS_FINALIZADO = 'Finalizado';
    const STATUS_NAO_FINALIZADO = 'Não Finalizado';

    const breadcrumbs = ['Admin','Rel. Intenção de Venda'];
    const [PesquisaRelatorioIntencaoVenda, setPesquisaRelatorioIntencaoVenda] = useState([]);
    const [totalTaxa, setTotalTaxa] = useState(0);
    const [totalValor, setTotalValor] = useState(0);
    const [PesquisaFiliados, setPesquisaFiliados] = useState([]);
    const [filtroTexto, setFiltroTexto] = useState('');

    const [exibeErro, setExibeErro] = useState(false);    
    const [mensagemErro, setMensagemErro] = useState('');    

    const [fi100_key_public, set100_key_public] = useState('0');
    const [mes, setmes] = useState(date.getMonth()+1);
    const [ano, setano] = useState(String(date.getFullYear()));
    const [anosDisponiveis, setAnosDisponiveis] = useState([]);

    const possuiValor = useCallback((valor) => {
        if (valor === null || valor === undefined) {
            return false;
        }
        if (typeof valor === 'string') {
            return valor.trim().length > 0;
        }
        return true;
    }, []);

    const valorOuTraco = useCallback(
        (valor) => (possuiValor(valor) ? String(valor) : '-'),
        [possuiValor],
    );

    const ehFinalizado = useCallback(
        (status) => String(status || '').trim().toLowerCase() === STATUS_FINALIZADO.toLowerCase(),
        [STATUS_FINALIZADO],
    );

    const obterStatusIntencaoVenda = useCallback((registro) => {
        if (possuiValor(registro?.status)) {
            return registro.status;
        }
        if (possuiValor(registro?.ve103_num_atpve)) {
            return STATUS_FINALIZADO;
        }
        return STATUS_NAO_FINALIZADO;
    }, [possuiValor]);

    const ordenarFinalizadosPrimeiro = useCallback(
        (registros = []) =>
            registros
                .map((item, indiceOriginal) => ({ item, indiceOriginal }))
                .sort((a, b) => {
                    const prioridadeA = ehFinalizado(a.item?.status) ? 0 : 1;
                    const prioridadeB = ehFinalizado(b.item?.status) ? 0 : 1;
                    if (prioridadeA !== prioridadeB) {
                        return prioridadeA - prioridadeB;
                    }
                    return a.indiceOriginal - b.indiceOriginal;
                })
                .map(({ item }) => item),
        [ehFinalizado],
    );

    const normalizarRegistrosIntencaoVenda = useCallback(
        (registros = []) =>
            ordenarFinalizadosPrimeiro(
                (Array.isArray(registros) ? registros : []).map((item) => ({
                    ...item,
                    status: obterStatusIntencaoVenda(item),
                })),
            ),
        [obterStatusIntencaoVenda, ordenarFinalizadosPrimeiro],
    );

    const obterCnpjFiliado = useCallback(
        (registro) => valorOuTraco(registro?.fi100_filiado?.fi100_num_cnpj || registro?.fi100_num_cnpj),
        [valorOuTraco],
    );

    const formatarDescricaoFiliado = useCallback((registro) => {
        const nome = registro?.fi100_filiado?.fi100_dsc_filiado || registro?.fi100_dsc_filiado || '-';
        const associadoRaw = registro?.fi100_filiado?.fi100_ind_associado ?? registro?.fi100_ind_associado ?? false;
        const associadoTexto = associadoRaw ? 'É associado' : 'Não é associado';
        return `${nome} - ${associadoTexto}`;
    }, []);

    useEffect(() => {
        fetchFiliados();
    }, []);

    useEffect(() => {
        fetchIntencaoVenda(fi100_key_public, mes, ano);
    }, [fi100_key_public, mes, ano]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchAnosIntencaoVenda(fi100_key_public);
    }, [fi100_key_public]);

    useEffect(() => {
        const anoAtual = String(ano);
        if (anosDisponiveis.length > 0 && !anosDisponiveis.includes(anoAtual)) {
            setano(anosDisponiveis[anosDisponiveis.length - 1]);
        }
    }, [anosDisponiveis, ano]);

    async function fetchIntencaoVenda(fKey,fMes,fAno){

        relatorioAdmin.get(`/intencaovenda/${fKey}/${fMes}/${fAno}`)
        .then(resposta => {
            const registros = normalizarRegistrosIntencaoVenda(resposta.data);
            setPesquisaRelatorioIntencaoVenda(registros);
            const taxaTotal = registros.reduce((acumulado, item) => {
                const bruto = item?.fi100_vlr_intencao_venda ?? item?.fi100_filiado?.fi100_vlr_intencao_venda ?? 0;
                return acumulado + normalizarValorNumerico(bruto);
            }, 0);
            const valorTotal = registros.reduce(
                (acumulado, item) => acumulado + normalizarValorNumerico(item.ve103_vlr_venda),
                0,
            );
            setTotalTaxa(taxaTotal);
            setTotalValor(valorTotal);
        })
        .catch(erro => {          
            setTotalTaxa(0);
            setTotalValor(0);
            if(erro?.resposta?.data?.message){
                setExibeErro(true);
                setMensagemErro(erro.resposta.data.message);
            }else{
                if (erro.response?.status === 401) {
                    setExibeErro(true);
                    setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                  }else{
                    setExibeErro(true);
                    setMensagemErro('Ops! Ocorreu um erro inesperado.');
                  }                                           
            }        
        })               
        
    }        

    async function fetchAnosIntencaoVenda(fKey){
        relatorioAdmin.get(`/intencaovenda/anos/${fKey}`)
        .then(resposta => {
            const anos = (resposta.data?.anos || []).map((item) => String(item));
            setAnosDisponiveis(anos);
        })
        .catch(erro => {          
            setAnosDisponiveis([]);
            if(erro?.resposta?.data?.message){
                setExibeErro(true);
                setMensagemErro(erro.resposta.data.message);
            }else{
                if (erro.response?.status === 401) {
                    setExibeErro(true);
                    setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                  }else{
                    setExibeErro(true);
                    setMensagemErro('Ops! Ocorreu um erro inesperado.');
                  }                                           
            }        
        })
    }        

    async function fetchFiliados(){

        filiadoAPI.get('/')
        .then(resposta => {
            setPesquisaFiliados(resposta.data);
        })
        .catch(erro => {          
            setTotalTaxa(0);
            setTotalValor(0);
            if(erro?.resposta?.data?.message){
                setExibeErro(true);
                setMensagemErro(erro.resposta.data.message);
            }else{
                if (erro.response?.status === 401) {
                    setExibeErro(true);
                    setMensagemErro('Ops! Sessão Expirada! Faça um novo login.');
                  }else{
                    setExibeErro(true);
                    setMensagemErro('Ops! Ocorreu um erro inesperado.');
                  }                                           
            }        
        })               
        
    }        


    const FormatarDataHora = useCallback((string) => {

        let retorno = '-';
        if(possuiValor(string)){
            const texto = String(string);
            const partes = texto.split("T");
            if (partes.length >= 2) {
                const dataaux = partes[0].split("-");
                if (dataaux.length === 3) {
                    const dataformatada = dataaux[2] + '/' + dataaux[1] + "/" + dataaux[0];
                    retorno = `${dataformatada} ${partes[1].substring(0,5)}`;
                }
            }
        }

        return retorno;

    }, [possuiValor]);

    const colunasBase = useMemo(() => [
        {
            id: 'filiado',
            header: () => 'Filiado',
            accessorFn: (row) => formatarDescricaoFiliado(row),
            className: 'tw-align-top tw-font-medium tw-text-slate-700 dark:tw-text-slate-200',
            label: 'Filiado',
            canHide: false,
        },
        {
            id: 'cnpj',
            header: () => 'CNPJ',
            accessorFn: (row) => obterCnpjFiliado(row),
            className: 'tw-whitespace-nowrap tw-text-slate-600 dark:tw-text-slate-300',
            label: 'CNPJ',
        },
        {
            id: 'placa',
            header: () => 'Placa',
            accessorKey: 've103_dsc_placa',
            cell: ({ value }) => valorOuTraco(value),
            className: 'tw-whitespace-nowrap tw-uppercase tw-font-semibold tw-text-slate-700 dark:tw-text-slate-100',
            label: 'Placa',
        },
        {
            id: 'renavam',
            header: () => 'Renavam',
            accessorKey: 've103_num_renavam',
            cell: ({ value }) => valorOuTraco(value),
            label: 'Renavam',
        },
        {
            id: 'crv',
            header: () => 'CRV',
            accessorKey: 've103_num_crv',
            cell: ({ value }) => valorOuTraco(value),
            label: 'CRV',
        },
        {
            id: 'documentoComprador',
            header: () => 'Documento Comprador',
            accessorKey: 've103_num_documento_comprador',
            cell: ({ value }) => valorOuTraco(value),
            label: 'Documento Comprador',
        },
        {
            id: 'comprador',
            header: () => 'Comprador',
            accessorKey: 've103_dsc_nome_comprador',
            cell: ({ value }) => valorOuTraco(value),
            label: 'Comprador',
        },
        {
            id: 'valor',
            header: () => 'Valor',
            accessorFn: (row) => normalizarValorNumerico(row.ve103_vlr_venda),
            cell: ({ value }) => formatarMoedaBRL(normalizarValorNumerico(value)),
            className: 'tw-whitespace-nowrap',
            label: 'Valor',
        },
        {
            id: 'taxa',
            header: () => 'Taxa por Intenção',
            accessorFn: (row) => normalizarValorNumerico(row?.fi100_vlr_intencao_venda ?? row?.fi100_filiado?.fi100_vlr_intencao_venda ?? 0),
            cell: ({ value }) => formatarMoedaBRL(normalizarValorNumerico(value)),
            className: 'tw-whitespace-nowrap',
            label: 'Taxa',
        },
        {
            id: 'atpve',
            header: () => 'ATPVe',
            accessorKey: 've103_num_atpve',
            cell: ({ value }) => valorOuTraco(value),
            label: 'ATPVe',
        },
        {
            id: 'dataCadastro',
            header: () => 'Data Cadastro',
            accessorFn: (row) => row.createdAt,
            cell: ({ value }) => FormatarDataHora(value),
            label: 'Data Cadastro',
        },
        {
            id: 'status',
            header: () => 'Status',
            accessorFn: (row) => obterStatusIntencaoVenda(row),
            cell: ({ value }) => valorOuTraco(value),
            className: 'tw-whitespace-nowrap',
            label: 'Status',
        },
        {
            id: 'situacao',
            header: () => 'Situação',
            accessorKey: 've103_dsc_situacao',
            cell: ({ value }) => valorOuTraco(value),
            label: 'Situação',
        },
        {
            id: 'dataSituacao',
            header: () => 'Data Situação',
            accessorFn: (row) => row.ve103_dat_situacao,
            cell: ({ value }) => FormatarDataHora(value),
            label: 'Data Situação',
        },
    ], [FormatarDataHora, formatarDescricaoFiliado, obterCnpjFiliado, obterStatusIntencaoVenda, valorOuTraco]);

    const [colunasVisiveis, setColunasVisiveis] = useState(() =>
        Object.fromEntries(colunasBase.map(coluna => [coluna.id, true])),
    );

    const colunasTabela = useMemo(
        () => colunasBase.filter(coluna => colunasVisiveis[coluna.id] !== false),
        [colunasBase, colunasVisiveis],
    );

    const cabecalhoExportacao = useMemo(
        () => [
            'Filiado',
            'CNPJ',
            'Placa',
            'Renavam',
            'CRV',
            'Documento Comprador',
            'Comprador',
            'Valor',
            'Taxa por Intenção',
            'ATPVe',
            'Data Cadastro',
            'Status',
            'Situação',
            'Data Situação',
        ],
        [],
    );

    const dadosFiltrados = useMemo(() => {
        const busca = filtroTexto.trim().toLowerCase();
        if (!busca) {
            return PesquisaRelatorioIntencaoVenda;
        }
        return PesquisaRelatorioIntencaoVenda.filter((item) => {
            const termos = [
                item?.fi100_filiado?.fi100_dsc_filiado,
                item?.fi100_filiado?.fi100_num_cnpj,
                item?.ve103_dsc_placa,
                item?.ve103_num_renavam,
                item?.ve103_num_crv,
                item?.ve103_num_documento_comprador,
                item?.ve103_dsc_nome_comprador,
                item?.ve103_num_atpve,
                obterStatusIntencaoVenda(item),
                item?.ve103_dsc_situacao,
            ];
            return termos.some((campo) => (campo || '').toString().toLowerCase().includes(busca));
        });
    }, [PesquisaRelatorioIntencaoVenda, filtroTexto, obterStatusIntencaoVenda]);

    const totaisFiltrados = useMemo(() => {
        const valor = dadosFiltrados.reduce(
            (acc, item) => acc + normalizarValorNumerico(item.ve103_vlr_venda),
            0,
        );
        const taxa = dadosFiltrados.reduce(
            (acc, item) => {
                const bruto = item?.fi100_vlr_intencao_venda ?? item?.fi100_filiado?.fi100_vlr_intencao_venda ?? 0;
                return acc + normalizarValorNumerico(bruto);
            },
            0,
        );
        return { valor, taxa };
    }, [dadosFiltrados]);

    const toggleColuna = (colunaId) => {
        const coluna = colunasBase.find(col => col.id === colunaId);
        if (coluna?.canHide === false) {
            return;
        }
        setColunasVisiveis((anteriores) => ({
            ...anteriores,
            [colunaId]: anteriores[colunaId] === false,
        }));
    };

    const handleExportarExcel = () => {
        const linhas = PesquisaRelatorioIntencaoVenda.map((ivenda) => [
            formatarDescricaoFiliado(ivenda),
            obterCnpjFiliado(ivenda),
            ivenda.ve103_dsc_placa || '',
            ivenda.ve103_num_renavam || '',
            ivenda.ve103_num_crv || '',
            ivenda.ve103_num_documento_comprador || '',
            ivenda.ve103_dsc_nome_comprador || '',
            normalizarValorNumerico(ivenda.ve103_vlr_venda),
            normalizarValorNumerico(ivenda?.fi100_vlr_intencao_venda ?? ivenda?.fi100_filiado?.fi100_vlr_intencao_venda ?? 0),
            valorOuTraco(ivenda.ve103_num_atpve),
            FormatarDataHora(ivenda.createdAt),
            obterStatusIntencaoVenda(ivenda),
            valorOuTraco(ivenda.ve103_dsc_situacao),
            FormatarDataHora(ivenda.ve103_dat_situacao),
        ]);

        linhas.push([
            'Totais',
            '',
            '',
            '',
            '',
            '',
            '',
            normalizarValorNumerico(totalValor),
            normalizarValorNumerico(totalTaxa),
            '',
            '',
            '',
            '',
            '',
        ]);

        exportarXLSX({
            arquivo: 'ConexaoDetranPR_IntencaoVenda',
            aba: 'Intenção de Venda',
            cabecalho: cabecalhoExportacao,
            linhas,
            colunasNumericas: [7, 8],
        });
    };

    return (
        <div class="row">
            <div class="container">
                <Topo titulo='Relatório Intenção de Venda' breadcrumbs={breadcrumbs} btnincluir={false} admin={true}/>
                <ListagemFeedback
                    displayErro={exibeErro}
                    mensagemErro={mensagemErro}
                />
            </div>
            <div class="admin-emplacar-card tw-mt-4 tw-rounded-3xl tw-border tw-border-slate-200/70 tw-bg-white/90 tw-p-6 tw-shadow-xl tw-backdrop-blur-sm dark:tw-border-slate-800/60 dark:tw-bg-slate-900/60 dark:tw-shadow-[0_20px_45px_rgba(8,15,32,0.45)]">
                <ListagemOpcoes
                    esconderTamanhoPagina={true}
                    esconderOpcaoFiltro={true}
                    esconderCampoFiltro={true}
                />
            <div class="row row-padding-conteudo tw-pt-2">
                <div class="col-12">
                    <div class="tw-flex tw-flex-col tw-gap-5 tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/90 tw-p-5 tw-shadow-sm tw-backdrop-blur dark:tw-border-slate-700/70 dark:tw-bg-slate-900/70 dark:tw-shadow-[0_20px_30px_rgba(8,15,32,0.35)]">
                        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                            <div class="tw-flex tw-items-center tw-gap-3">
                                <label for="fi100_key_public" class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 dark:tw-text-white tw-leading-none">
                                    Filiado:
                                </label>
                                <select
                                    class="form-select tw-h-11 tw-min-w-[220px] tw-border-slate-200/70 tw-bg-white/95 dark:tw-border-slate-700 dark:tw-text-white dark:tw-!bg-slate-900/70"
                                    aria-label="Selecione"
                                    id="fi100_key_public"
                                    name="fi100_key_public"
                                    onChange={evento => { set100_key_public(evento.target.value); fetchIntencaoVenda(evento.target.value, mes, ano); }}
                                    value={fi100_key_public}
                                >
                                    <option value="0">Selecione</option>
                                    {PesquisaFiliados.map(filiado => (
                                        <option value={filiado.fi100_key_public}>{filiado.fi100_dsc_filiado}</option>
                                    ))}
                                </select>
                            </div>
                            <div class="tw-flex tw-items-center tw-gap-3">
                                <label for="mes" class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 dark:tw-text-white tw-leading-none">
                                    Mês:
                                </label>
                                <select
                                    class="form-select tw-h-11 tw-min-w-[140px] tw-border-slate-200/70 tw-bg-white/95 dark:tw-border-slate-700 dark:tw-text-white dark:tw-!bg-slate-900/70"
                                    aria-label="Selecione"
                                    id="mes"
                                    name="mes"
                                    onChange={evento => { setmes(evento.target.value); fetchIntencaoVenda(fi100_key_public, evento.target.value, ano); }}
                                    value={mes}
                                >
                                    <option value="1">Janeiro</option>
                                    <option value="2">Fevereiro</option>
                                    <option value="3">Março</option>
                                    <option value="4">Abril</option>
                                    <option value="5">Maio</option>
                                    <option value="6">Junho</option>
                                    <option value="7">Julho</option>
                                    <option value="8">Agosto</option>
                                    <option value="9">Setembro</option>
                                    <option value="10">Outubro</option>
                                    <option value="11">Novembro</option>
                                    <option value="12">Dezembro</option>
                                </select>
                            </div>
                            <div class="tw-flex tw-items-center tw-gap-3">
                                <label for="ano" class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 dark:tw-text-white tw-leading-none">
                                    Ano:
                                </label>
                                <select
                                    class="form-select tw-h-11 tw-min-w-[120px] tw-border-slate-200/70 tw-bg-white/95 dark:tw-border-slate-700 dark:tw-text-white dark:tw-!bg-slate-900/70"
                                    aria-label="Selecione"
                                    id="ano"
                                    name="ano"
                                    onChange={evento => { setano(evento.target.value); fetchIntencaoVenda(fi100_key_public, mes, evento.target.value); }}
                                    value={ano}
                                >
                                    {anosDisponiveis.map((item) => (
                                        <option key={item} value={item}>{item}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
                            <div class="tw-flex tw-min-w-[220px] tw-max-w-[680px] tw-flex-1 tw-items-center tw-gap-3">
                                <label for="filtro-intencao" class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 dark:tw-text-slate-300">
                                    Pesquisa:
                                </label>
                                <input
                                    id="filtro-intencao"
                                    type="search"
                                    class="form-control tw-h-11 tw-flex-1 tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white/95 tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700 placeholder:tw-text-slate-400 focus:tw-border-indigo-400 focus:tw-ring-2 focus:tw-ring-indigo-300 dark:tw-border-slate-700 dark:tw-!bg-slate-900/70 dark:tw-text-slate-100 dark:placeholder:tw-text-slate-500 dark:focus:tw-ring-indigo-400/40"
                                    placeholder="Filtrar por filiado, placa, documento..."
                                    value={filtroTexto}
                                    onChange={evento => setFiltroTexto(evento.target.value)}
                                />
                            </div>
                            <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-text-sm tw-font-semibold tw-text-slate-600 dark:tw-text-white">
                                <span class="tw-flex tw-items-center tw-gap-1">
                                    <span>Valor Total:</span>
                                    <span class="tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{formatarMoedaBRL(totaisFiltrados.valor)}</span>
                                </span>
                                <span class="tw-flex tw-items-center tw-gap-1">
                                    <span>Taxa Total:</span>
                                    <span class="tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{formatarMoedaBRL(totaisFiltrados.taxa)}</span>
                                </span>
                            </div>
                            <div class="tw-flex tw-items-center tw-gap-3 tw-ml-auto">
                                <ColumnToggleDropdown
                                    colunas={colunasBase}
                                    visibilidade={colunasVisiveis}
                                    onToggle={toggleColuna}
                                    label="Colunas"
                                    align="end"
                                />
                                <button
                                    type="button"
                                    class="btn-export-excel dark:tw-shadow-none tw-min-w-[160px]"
                                    onClick={handleExportarExcel}
                                >
                                    <span class="material-symbols-outlined">cloud_download</span>
                                    <span>Exportar Excel</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row row-padding-conteudo">
                <div class="col-12 mobile-hide tablet-hide">
                    <div class="admin-emplacar-table-wrapper table-responvive">
                        <DataTable
                            columns={colunasTabela}
                            data={dadosFiltrados}
                            getRowId={(row, index) => row.ve103_key_public || index}
                            className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-shadow-lg dark:tw-border-slate-800 dark:tw-bg-slate-900"
                        />
                    </div>
                </div>
            </div>
            <Paginacao esconder={true} />
            </div>
        </div>
    );
}


export default TabelaRelatorioIntencaoVenda;
